import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { NEWS_ITEMS } from '../utils/constants';

const Home = () => {
  const [currentSubtext, setCurrentSubtext] = useState(0);
  const subtexts = [
    'Shaping the Future of Artificial Intelligence',
    'Pioneering the Next Wave of Robotics',
    "Engineering Tomorrow's Technology"
  ];

  useEffect(() => {
  const interval = setInterval(() => {
    setCurrentSubtext((prev) => (prev + 1) % subtexts.length);
  }, 2800);
  return () => clearInterval(interval);
}, [subtexts.length]);


  const scrollToNews = () => {
    document.getElementById('latest-news')?.scrollIntoView({ behavior: 'smooth' });
  };

  const researchProjects = [
    {
      title: 'Neuromorphic AI',
      slug: 'neuromorphic-ai',
      desc: 'Developing brain-inspired hardware and software architectures for ultra-efficient real-time learning.'
    },
    {
      title: 'Hybrid AI Systems',
      slug: 'hybrid-ai-systems',
      desc: 'Integrating symbolic reasoning with deep learning to create explainable and logically sound AI.'
    },
    {
      title: 'Cognitive Neural Networks',
      slug: 'cognitive-neural-networks',
      desc: 'Building neural architectures with integrated long-term memory and human-like attention mechanisms.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* --- 1. HERO SECTION --- */}
      <section className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-7xl md:text-9xl font-bold mb-12 tracking-tight"
          >
            Neufora
          </motion.h1>

          <div className="h-20 flex items-center justify-center mb-16">
            <motion.p
              key={currentSubtext}
              initial={{ opacity: 0, filter: 'blur(10px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.6 }}
              className="text-xl md:text-2xl text-white/70 max-w-3xl"
            >
              {subtexts[currentSubtext]}
            </motion.p>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              onClick={scrollToNews}
              className="px-8 py-4 bg-white text-black rounded-2xl font-medium hover:scale-[1.02] transition-transform duration-200"
            >
              Explore
            </button>
            <Link
              to="/contact"
              className="px-8 py-4 border-2 border-white/30 rounded-2xl font-medium hover:border-white/60 hover:scale-[1.02] transition-all duration-200"
            >
              Contact
            </Link>
          </div>
        </div>
      </section>

      {/* --- 2. LATEST NEWS SECTION (Fixed Equal Height Boxes) --- */}
      <section id="latest-news" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">Latest News</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch"> {/* items-stretch ensures row height match */}
            {NEWS_ITEMS.map((news, index) => (
              <motion.div
                key={news.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="flex h-full" 
              >
                <Link to={`/news/${news.slug}`} className="block group w-full h-full">
                  <div className="bg-white/5 rounded-3xl overflow-hidden border border-white/10 hover:border-white/30 transition-all duration-300 h-full flex flex-col">
                    {/* Top half: Placeholder image */}
                    {/* Top half: Image or placeholder */}
{news.image ? (
  <div className="h-56 overflow-hidden shrink-0">
    <img
      src={news.image}
      alt={news.title}
      className="w-full h-full object-cover"
    />
  </div>
) : (
  <div className="h-56 bg-gradient-to-br from-white/10 to-white/5 shrink-0" />
)}

                    
                    {/* Bottom half: Royal Navy Glassy Blue */}
                    <div className="p-8 bg-[#0a1630]/60 backdrop-blur-xl flex-grow flex flex-col border-t border-white/5">
                      <h3 className="text-xl font-semibold mb-3 text-white">
                        {news.title}
                      </h3>
                      <p className="text-white/60 mb-6 leading-relaxed line-clamp-3 flex-grow">
                        {news.excerpt}
                      </p>
                      <span className="inline-flex items-center text-sm font-medium text-white/70 group-hover:text-white transition-colors mt-auto">
                        Read Full Article
                        <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 3. SERUMAI SECTION --- */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-white/90">Meet Nextgen AI</h2>
            <h3 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                SerumAI
              </span>
            </h3>
            <p className="text-xl text-white/60 mb-8">Smart Enhanced Reactive Universal Machine (SERUM)</p>
            <p className="text-white/50 mb-12 leading-relaxed max-w-2xl mx-auto">
              A revolutionary artificial intelligence architecture that combines advanced neural processing with adaptive learning capabilities.
            </p>
            <Link
              to="/coming-soon"
              className="inline-flex items-center px-8 py-4 bg-white text-black rounded-2xl font-medium hover:scale-[1.02] transition-transform duration-200"
            >
              Explore <ArrowRight size={20} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* --- 4. RESEARCH OVERVIEW SECTION --- */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">Research</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {researchProjects.map((project, index) => (
              <motion.div
                key={project.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Link to={`/research/${project.slug}`} className="block group">
                  <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:border-white/30 transition-all duration-300 h-full">
                    <h3 className="text-2xl font-semibold mb-4">{project.title}</h3>
                    <p className="text-white/60 leading-relaxed mb-6">{project.desc}</p>
                    <span className="inline-flex items-center text-white/70 group-hover:text-white transition-colors">
                      Learn More <ChevronRight size={18} className="ml-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 5. FEATURE SECTIONS (ROBOTICS & CHIP) --- */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-stretch">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="h-full flex">
            <Link to="/research/autonomous-robotics" className="block h-full group w-full">
              <div className="h-full bg-[#0a1630]/60 backdrop-blur-lg rounded-2xl p-8 border border-white/10 transition-all duration-300 flex flex-col hover:border-white/60">
                <h3 className="text-3xl font-bold mb-4">Autonomous Robotics</h3>
                <p className="text-white/60 leading-relaxed mb-6">
                  Pioneering self-operating robotic systems that can navigate, learn, and adapt to dynamic environments.
                </p>
                <span className="mt-auto inline-flex items-center text-white/70 group-hover:text-white transition-colors">
                  Discover More <ChevronRight size={18} className="ml-1" />
                </span>
              </div>
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="h-full flex">
            <Link to="/research/pryma-h1" className="block h-full group w-full">
              <div className="h-full bg-[#0a1630]/60 backdrop-blur-lg rounded-2xl p-8 border border-white/10 transition-all duration-300 flex flex-col hover:border-white/60">
                <h3 className="text-3xl font-bold mb-2">Pryma-H1</h3>
                <p className="text-sm text-white/50 mb-4">Neural Chip</p>
                <p className="text-white/60 leading-relaxed mb-6">
                  Next-generation neural processing chip designed to accelerate AI computations with peak efficiency.
                </p>
                <span className="mt-auto inline-flex items-center text-white/70 group-hover:text-white transition-colors">
                  Discover More <ChevronRight size={18} className="ml-1" />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* --- 6. BUSINESS SECTIONS (APIs & INVEST) --- */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Link to="/coming-soon" className="block group">
              <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-10 border border-white/10 hover:border-white/30 transition-all duration-300 text-center h-full">
                <h3 className="text-3xl font-bold mb-4">APIs</h3>
                <p className="text-white/60 leading-relaxed mb-6">
                  Integrate Neufora's powerful AI capabilities into your applications with our developer-friendly API solutions.
                </p>
                <span className="inline-flex items-center text-white/70 group-hover:text-white transition-colors">
                  Coming Soon <ChevronRight size={18} className="ml-1" />
                </span>
              </div>
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Link to="/invest" className="block group">
              <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-10 border border-white/10 hover:border-white/30 transition-all duration-300 text-center h-full">
                <h3 className="text-3xl font-bold mb-4">Invest in Neufora</h3>
                <p className="text-white/60 leading-relaxed mb-6">
                  Join us in shaping the future of artificial intelligence and robotics. Explore investment opportunities.
                </p>
                <span className="inline-flex items-center text-white/70 group-hover:text-white transition-colors">
                  Learn More <ChevronRight size={18} className="ml-1" />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;